import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  MdClose, MdSend, MdHeadset, MdEmail, MdPhone, MdCheck,
  MdKeyboardArrowDown, MdKeyboardArrowUp
} from 'react-icons/md';
import { useModal } from '../context/ModalContext';

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const Widget = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  width: 360px;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  overflow: hidden;
  animation: ${slideUp} 0.25s ease-out;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  cursor: ${p => p.clickable ? 'pointer' : 'default'};
  user-select: none;
  flex-shrink: 0;
`;

const HeaderIcon = styled.div`
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
  }

  p {
    margin: 2px 0 0;
    font-size: 0.72rem;
    opacity: 0.85;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: rgba(255,255,255,0.2);
  border-radius: 8px;
  color: white;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255,255,255,0.35);
  }
`;

const Body = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  overflow-y: auto;
  max-height: 480px;
  padding: 16px;

  ${p => !p.visible && css`
    max-height: 0;
    padding: 0;
    overflow: hidden;
  `}

  transition: max-height 0.3s ease, padding 0.3s ease;
`;

const InfoCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
`;

const InfoCard = styled.div`
  background: ${p => p.theme.colors.background};
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${p => p.theme.colors.border};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 2px 8px ${p => p.theme.colors.primary}20;
  }

  svg {
    color: ${p => p.theme.colors.primary};
    font-size: 18px;
    flex-shrink: 0;
  }
`;

const InfoCardText = styled.div`
  min-width: 0;

  p {
    margin: 0;
    font-size: 0.65rem;
    color: ${p => p.theme.colors.textSecondary};
  }

  a {
    font-size: 0.75rem;
    color: ${p => p.theme.colors.textPrimary};
    font-weight: 500;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;

    &:hover { color: ${p => p.theme.colors.primary}; }
  }
`;

const Divider = styled.div`
  border: none;
  border-top: 1px solid ${p => p.theme.colors.border};
  margin: 0 0 14px;
`;

const SectionTitle = styled.h4`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 12px;
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 500;
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  font-size: 0.8rem;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: inherit;
  transition: border-color 0.15s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}25;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  font-size: 0.8rem;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  font-size: 0.8rem;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.15s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}25;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 0.68rem;
  color: ${p => p.theme.colors.textLight};
  margin-top: 3px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  flex-shrink: 0;
`;

const Btn = styled.button`
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  transition: all 0.15s;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CancelBtn = styled(Btn)`
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textSecondary};
  border: 1px solid ${p => p.theme.colors.border};

  &:hover:not(:disabled) { background: ${p => p.theme.colors.background}; }
`;

const SendBtn = styled(Btn)`
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px ${p => p.theme.colors.primary}50;
    transform: translateY(-1px);
  }
`;

const SuccessMsg = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 10px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  border: 1px solid #c3e6cb;
  font-size: 0.8rem;

  svg { font-size: 20px; flex-shrink: 0; color: #155724; }
`;

const SupportModal = ({ isOpen, onClose }) => {
  const { toast } = useModal();
  const [minimized, setMinimized] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '', email: '', asunto: 'general', mensaje: ''
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const maxChars = 500;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mensaje' && value.length > maxChars) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email || !formData.mensaje) {
      toast('Por favor completa todos los campos', 'warning');
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSuccess(true);
      setTimeout(() => {
        setFormData({ nombre: '', email: '', asunto: 'general', mensaje: '' });
        setSuccess(false);
        onClose();
      }, 2000);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <Widget>
      <Header clickable onClick={() => setMinimized(m => !m)}>
        <HeaderIcon>
          <MdHeadset />
        </HeaderIcon>
        <HeaderTitle>
          <h3>Centro de Soporte</h3>
          {!minimized && <p>Estamos aquí para ayudarte</p>}
        </HeaderTitle>
        <HeaderActions onClick={e => e.stopPropagation()}>
          <IconBtn
            title={minimized ? 'Expandir' : 'Minimizar'}
            onClick={() => setMinimized(m => !m)}
          >
            {minimized ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconBtn>
          <IconBtn title="Cerrar" onClick={onClose}>
            <MdClose />
          </IconBtn>
        </HeaderActions>
      </Header>

      <Body visible={!minimized}>
        {success && (
          <SuccessMsg>
            <MdCheck />
            <div>
              <strong>¡Mensaje enviado!</strong>
              <div>Nuestro equipo te responderá en breve.</div>
            </div>
          </SuccessMsg>
        )}

        <InfoCards>
          <InfoCard>
            <MdEmail />
            <InfoCardText>
              <p>Email</p>
              <a href="mailto:soporte@paymentmanager.com">soporte@pm.com</a>
            </InfoCardText>
          </InfoCard>
          <InfoCard>
            <MdPhone />
            <InfoCardText>
              <p>Teléfono</p>
              <a href="tel:+525512345678">+52 55 1234 5678</a>
            </InfoCardText>
          </InfoCard>
        </InfoCards>

        <Divider />
        <SectionTitle>Enviar Mensaje</SectionTitle>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Nombre Completo *</Label>
            <Input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="Tu nombre"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Correo Electrónico *</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Asunto</Label>
            <Select name="asunto" value={formData.asunto} onChange={handleChange}>
              <option value="general">Consulta General</option>
              <option value="tecnico">Problema Técnico</option>
              <option value="facturacion">Facturación</option>
              <option value="feature">Solicitud de Funcionalidad</option>
              <option value="otro">Otro</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Mensaje *</Label>
            <TextArea
              name="mensaje"
              value={formData.mensaje}
              onChange={handleChange}
              placeholder="Describe tu consulta o problema..."
              required
            />
            <CharCount>{formData.mensaje.length} / {maxChars}</CharCount>
          </FormGroup>
        </form>
      </Body>

      {!minimized && (
        <Footer>
          <CancelBtn type="button" onClick={onClose} disabled={sending}>
            Cancelar
          </CancelBtn>
          <SendBtn onClick={handleSubmit} disabled={sending || success}>
            {sending ? 'Enviando...' : success ? <><MdCheck /> Enviado</> : <><MdSend /> Enviar</>}
          </SendBtn>
        </Footer>
      )}
    </Widget>
  );
};

export default SupportModal;
